import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bell, BellOff, Copy, Pause, Pencil, Play, Trash2, Zap } from "lucide-react";
import PriceChart from "../components/PriceChart";
import { getBars, getLiveTail, type BarOut } from "../api/bars";
import { parseTs } from "../lib/time";
import { isGoldenFamily } from "../lib/goldenFamily";
import { getStrategy } from "../api/strategies";
import {
  buildInstanceFromSpec,
  type IndicatorInstance,
} from "../lib/indicators";
import type { BacktestTrade } from "../api/backtests";
import {
  cloneDeployment,
  deleteDeployment,
  getAbandonedPosition,
  getDeployment,
  getDeploymentGoldenNumbers,
  getDeploymentLiveBars,
  getDeploymentDayState,
  getDeploymentSummary,
  type DeploymentSummary,
  killDeployment,
  listDeploymentFills,
  listDeploymentOrders,
  reconnectFeed,
  resetDeploymentDay,
  setDeploymentNotifications,
  startDeployment,
  stopDeployment,
  type Deployment,
  type DayStopReason,
  type DeploymentCloneRequest,
  type DeploymentPositionView,
  type FillEvent,
  type OpenPositionChoice,
  type OrderEvent,
} from "../api/deployments";
import { getUser } from "../api/admin";
import { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import AdminStopKillDialog, { type AdminAction } from "../components/AdminStopKillDialog";
import Card from "../components/Card";
import Button from "../components/Button";
import Chip from "../components/Chip";
import DataTable from "../components/DataTable";
import EmptyState from "../components/EmptyState";
import StatusPill from "../components/StatusPill";
import { Tabs } from "../components/Tabs";
import {
  formatINR,
  formatInt,
  formatIst,
  signedClass,
} from "../lib/format";

const RUNNING_STATUSES = new Set(["RUNNING", "STARTING", "STOPPING"]);

export default function DeploymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const { data: d, isLoading } = useQuery({
    queryKey: ["deployment", id],
    queryFn: () => getDeployment(id!),
    enabled: !!id,
    refetchInterval: 3_000,
  });

  // An admin looking at someone else's deployment: read-only, except Stop and
  // Kill (which go through a confirmation and notify the owner). The server
  // enforces this too — every other action 404s for a non-owner.
  const asAdmin = !!d && !!me && d.user_id !== me.id;
  const { data: owner } = useQuery({
    queryKey: ["admin-user", d?.user_id],
    queryFn: () => getUser(d!.user_id),
    enabled: asAdmin,
  });
  const ownerLabel = owner?.display_name || owner?.email || "this user";
  const [adminAction, setAdminAction] = useState<AdminAction | null>(null);

  const { data: summary } = useQuery({
    queryKey: ["deployment-summary", id],
    queryFn: () => getDeploymentSummary(id!),
    enabled: !!id && !!d && RUNNING_STATUSES.has(d.status),
    refetchInterval: 1_000,
  });

  const { data: orders } = useQuery({
    queryKey: ["deployment-orders", id],
    queryFn: () => listDeploymentOrders(id!, { limit: 200 }),
    enabled: !!id,
    refetchInterval: d && RUNNING_STATUSES.has(d.status) ? 5_000 : false,
  });

  const { data: fills } = useQuery({
    queryKey: ["deployment-fills", id],
    queryFn: () => listDeploymentFills(id!, { limit: 200 }),
    enabled: !!id,
    refetchInterval: d && RUNNING_STATUSES.has(d.status) ? 5_000 : false,
  });

  // Durable day state. Unlike the summary this is polled even while STOPPED —
  // the whole point is that it outlives the runner, and the max-loss warning
  // has to show on a stopped deployment for the user to act on it.
  const { data: dayState } = useQuery({
    queryKey: ["deployment-day-state", id],
    queryFn: () => getDeploymentDayState(id!),
    enabled: !!id,
    refetchInterval: 10_000,
  });
  // Which gate stopped the day. Old rows carry no reason: a day that stopped
  // in the black then reads as a profit stop, as it always did.
  const dayStopKind: DayStopReason | null = !dayState?.max_loss_hit
    ? null
    : (dayState.stop_reason ??
      (Number(dayState.realized_rupees) > 0 ? "profit" : "max_loss"));

  // A position a previous Stop left with the broker. Polled while stopped for
  // the same reason as the day state: the prompt has to appear BEFORE the user
  // presses Start, not as an error afterwards.
  const { data: abandoned } = useQuery({
    queryKey: ["deployment-abandoned-position", id],
    queryFn: () => getAbandonedPosition(id!),
    enabled: !!id,
    refetchInterval: 10_000,
  });

  const [actionError, setActionError] = useState<string | null>(null);
  // Clone panel state. Kept here rather than in a child so the panel survives
  // the 3s deployment refetch.
  // `?clone=1` opens the panel straight away — that is how the Clone button on
  // the deployments list arrives here, so both routes lead to the same picker
  // rather than one of them silently duplicating the source's mode.
  const [searchParams, setSearchParams] = useSearchParams();
  const [cloning, setCloning] = useState(searchParams.get("clone") === "1");
  const [cloneName, setCloneName] = useState("");
  const [cloneMode, setCloneMode] = useState<"paper" | "live">("paper");
  const [cloneNotice, setCloneNotice] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: (openPosition?: OpenPositionChoice) =>
      startDeployment(id!, openPosition),
    onSuccess: () => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["deployment", id] });
      qc.invalidateQueries({ queryKey: ["deployment-abandoned-position", id] });
    },
    // A start refused because the day already hit max loss — or because a
    // carried position hasn't been answered for — comes back as a 409 with the
    // explanation. Surface it instead of failing silently.
    onError: (e) => setActionError(apiErrorMessage(e)),
  });
  const resetDay = useMutation({
    mutationFn: () => resetDeploymentDay(id!),
    onSuccess: () => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["deployment-day-state", id] });
    },
    onError: (e) => setActionError(apiErrorMessage(e)),
  });
  const stop = useMutation({
    mutationFn: () => stopDeployment(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deployment", id] }),
  });
  const kill = useMutation({
    mutationFn: () => killDeployment(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deployment", id] }),
  });
  const del = useMutation({
    mutationFn: () => deleteDeployment(id!),
    onSuccess: () => navigate("/deployments"),
  });
  // Cloning is how a mode changes: the config is copied into a NEW deployment
  // and the source keeps its history. Allowed while running — nothing about
  // the original is touched.
  const clone = useMutation({
    mutationFn: (payload: DeploymentCloneRequest) => cloneDeployment(id!, payload),
    onSuccess: (created) => {
      // Close the panel BEFORE navigating. Moving between /deployments/:id
      // routes only changes the param — this component stays mounted, so
      // `cloning` would survive and the form would sit there looking as if
      // nothing had happened. That is how one clone became several.
      setCloning(false);
      setCloneName("");
      setCloneNotice(`Created "${created.name}" — you are now viewing it.`);
      qc.invalidateQueries({ queryKey: ["deployments"] });
      navigate(`/deployments/${created.id}`);
    },
  });
  const notify = useMutation({
    mutationFn: (enabled: boolean) => setDeploymentNotifications(id!, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deployment", id] }),
  });
  // Arriving with ?clone=1 (from the deployments list) opens the panel. Seed
  // the mode from the source once it loads, then drop the parameter so a
  // refresh does not reopen the panel. In an effect, not in render: changing
  // the URL is a side effect, and doing it during render risks a loop.
  useEffect(() => {
    // Route param changed (including right after a clone) — this component is
    // reused, so its state must be cleared by hand.
    setCloning(false);
    setCloneName("");
  }, [id]);

  const cloneParam = searchParams.get("clone");
  useEffect(() => {
    if (cloneParam !== "1" || !d) return;
    setCloneMode(d.mode);
    setCloneName("");
    setCloning(true);
    setSearchParams({}, { replace: true });
  }, [cloneParam, d, setSearchParams]);

  const reconnect = useMutation({
    mutationFn: () => reconnectFeed(id!),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["deployment-summary", id] }),
  });

  if (isLoading || !d) return <div className="text-sm text-muted">Loading…</div>;

  const running = RUNNING_STATUSES.has(d.status);
  // KILLED is terminal like STOPPED/ERROR — without it here, a killed
  // deployment had all three buttons disabled with no way back (2026-07-31).
  const startable =
    !running &&
    !d.policy_blocked &&
    (d.status === "CREATED" ||
      d.status === "STOPPED" ||
      d.status === "ERROR" ||
      d.status === "KILLED");

  return (
    <div className="space-y-6">
      <Link
        to={asAdmin ? `/admin/users/${d.user_id}` : "/deployments"}
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-fg"
      >
        <ArrowLeft size={14} /> {asAdmin ? `Back to ${ownerLabel}` : "Back to deployments"}
      </Link>

      {asAdmin && (
        <div className="text-sm text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-400/30 rounded px-3 py-2">
          <span className="font-semibold">Admin view of {ownerLabel}&apos;s deployment.</span>{" "}
          Read-only — you can Stop or Kill it, but not start, edit, clone or delete
          it. {ownerLabel} is notified of any Stop or Kill.
        </div>
      )}
      {asAdmin && adminAction && (
        <AdminStopKillDialog
          target={{ id: d.id, name: d.name, mode: d.mode, ownerLabel }}
          action={adminAction}
          onClose={() => setAdminAction(null)}
        />
      )}

      {/* Header strip with key live numbers. Pinned from lg: only — it holds
          the title row, the action buttons and six stat tiles, which on a
          phone stacks to most of the viewport and left nothing below it
          usable. Below lg it scrolls away like any other card. `top-12`
          clears the sticky TopBar, which stays pinned at every width. */}
      <div className="bg-surface border border-border rounded-lg shadow-card lg:sticky lg:top-12 lg:z-[5]">
        <div className="px-4 py-3 sm:px-5 sm:py-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg sm:text-xl font-semibold text-fg break-words">{d.name}</h1>
              <StatusPill status={d.status} />
              <Chip tone={d.mode === "live" ? "warn" : "info"} mono>
                {d.mode}
              </Chip>
              <Chip mono>{d.strategy_key}</Chip>
            </div>
          </div>
          {asAdmin ? (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="secondary"
                disabled={!running}
                onClick={() => setAdminAction("stop")}
                iconLeft={<Pause size={14} />}
                title="Halts the deployment and leaves any open position with the broker"
              >
                Stop
              </Button>
              <Button
                variant="danger"
                disabled={!running}
                onClick={() => setAdminAction("kill")}
                iconLeft={<Zap size={14} />}
              >
                Kill
              </Button>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <Button
                // With a position carried over, the only way to start is through
                // the banner below, which makes the choice explicit. Leaving this
                // enabled would just 409.
                disabled={!startable || start.isPending || !!abandoned}
                onClick={() => start.mutate(undefined)}
                iconLeft={<Play size={14} />}
                title={
                  abandoned
                    ? "Choose what to do with the open position first"
                    : undefined
                }
              >
                Start
              </Button>
              <Button
                variant="secondary"
                disabled={!running || stop.isPending}
                onClick={() => stop.mutate()}
                iconLeft={<Pause size={14} />}
                title="Halts the deployment and leaves any open position with the broker"
              >
                Stop
              </Button>
              <Button
                variant="danger"
                disabled={!running || kill.isPending}
                onClick={() => {
                  if (confirm("Engage kill switch — block new orders and stop?")) kill.mutate();
                }}
                iconLeft={<Zap size={14} />}
              >
                Kill
              </Button>
              <button
                type="button"
                disabled={notify.isPending}
                onClick={() => notify.mutate(!d.notify_telegram)}
                title={
                  d.notify_telegram
                    ? "Telegram alerts ON — click to turn off"
                    : "Telegram alerts OFF — click to turn on"
                }
                aria-label={
                  d.notify_telegram ? "Telegram alerts on" : "Telegram alerts off"
                }
                className={`inline-flex items-center justify-center h-9 w-9 rounded-md border border-border hover:bg-surface-2 disabled:opacity-50 ${
                  d.notify_telegram
                    ? "text-up-600 dark:text-up-400"
                    : "text-down-600 dark:text-down-400"
                }`}
              >
                {d.notify_telegram ? <Bell size={16} /> : <BellOff size={16} />}
              </button>
              <Link to={running ? "#" : `/deployments/${d.id}/edit`}>
                <Button
                  variant="secondary"
                  disabled={running}
                  title={running ? "Stop the deployment to edit its configuration" : undefined}
                  iconLeft={<Pencil size={14} />}
                >
                  Edit
                </Button>
              </Link>
              <Button
                variant="secondary"
                disabled={clone.isPending}
                onClick={() => {
                  // Seed from the source each time the panel opens, so the
                  // default is a straight duplicate rather than whatever was
                  // last picked.
                  if (!cloning) {
                    setCloneMode(d.mode);
                    setCloneName("");
                  }
                  setCloning((v) => !v);
                }}
                title="Copy this configuration into a new deployment — the way to move between paper and live"
                iconLeft={<Copy size={14} />}
              >
                Clone
              </Button>
              <Button
                variant="ghost"
                disabled={running || del.isPending}
                onClick={() => {
                  if (
                    confirm(
                      `Delete deployment "${d.name}"?\n\nThis also removes its ` +
                        `order history, fills and day state. It cannot be undone.`,
                    )
                  )
                    del.mutate();
                }}
                title={
                  running
                    ? "Stop the deployment before deleting it"
                    : "Delete this deployment and its history"
                }
                iconLeft={<Trash2 size={14} />}
              >
                Delete
              </Button>
            </div>
          )}
        </div>
        {/* These numbers all came from the runner, which is disposed on stop
            and on kill — so the whole strip used to blank, reading as "nothing
            happened" rather than "not running". It is now rebuilt from the
            durable tables, and labelled so it is not mistaken for live. */}
        {summary && !summary.live && (
          <div className="border-t border-border px-5 pt-2 text-[10px] uppercase tracking-wide text-muted">
            Last known — not running. Rebuilt from the recorded orders, fills
            and day state.
          </div>
        )}
        <div className="border-t border-border px-4 py-3 sm:px-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 text-xs">
          <RuntimeStat
            label="Realized P&L"
            value={
              // Day-scoped: includes anything booked earlier today in a previous
              // runner session, so a stop/restart no longer appears to wipe the
              // day. Falls back to the session figure on an older backend.
              // While stopped there is no summary, so show the durable day row.
              (() => {
                const v =
                  summary?.day_realized_pnl ??
                  summary?.realized_pnl ??
                  dayState?.realized_rupees ??
                  0;
                return (
                  <span
                    className={signedClass(v)}
                    title="Realized P&L for the whole IST day, including earlier runs of the same day"
                  >
                    {formatINR(v, { sign: true })}
                  </span>
                );
              })()
            }
          />
          <RuntimeStat
            label="Unrealized P&L"
            value={
              <span className={signedClass(summary?.total_unrealized_pnl ?? 0)}>
                {formatINR(summary?.total_unrealized_pnl ?? 0, { sign: true })}
              </span>
            }
          />
          {summary?.realized_points_pnl != null && (
            <RuntimeStat
              label="Realized pts"
              value={
                <span
                  className={signedClass(summary.realized_points_pnl)}
                  title="Realized P&L today in deployment-symbol (underlying) points"
                >
                  {fmtPts(summary.realized_points_pnl)}
                </span>
              }
            />
          )}
          {summary?.overall_points_pnl != null && (
            <RuntimeStat
              label="Overall pts"
              value={
                <span
                  className={signedClass(summary.overall_points_pnl)}
                  title="Realized + open-position unrealized, in deployment-symbol (underlying) points"
                >
                  {fmtPts(summary.overall_points_pnl)}
                </span>
              }
            />
          )}
          <RuntimeStat
            label="Open positions"
            value={
              <span className="text-fg">
                {summary?.positions.filter((p) => Number(p.quantity) !== 0).length ?? 0}
              </span>
            }
          />
          <RuntimeStat
            label="Invested"
            value={
              <span className="text-fg" title="Capital in open trades: Σ |qty| × avg entry price">
                {formatINR(summary?.total_invested ?? 0)}
              </span>
            }
          />
          <RuntimeStat
            label="Feed"
            value={
              <FeedHealth
                running={running}
                secondsSinceTick={summary?.seconds_since_last_tick ?? null}
                ticks={summary?.ticks_processed}
                alert={summary?.feed_alert ?? null}
                onReconnect={asAdmin ? undefined : () => reconnect.mutate()}
                reconnecting={reconnect.isPending}
                reconnectError={
                  reconnect.isPending
                    ? null
                    : reconnect.error
                      ? String((reconnect.error as Error).message ?? reconnect.error)
                      : reconnect.data && !reconnect.data.ok
                        ? (reconnect.data.reason ?? "The feed did not reconnect.")
                        : null
                }
              />
            }
          />
          <RuntimeStat
            label="Orders / rejects / unfilled"
            title={
              "Rejects never reached the order book. Unfilled were accepted by " +
              "the broker but confirmed not to have filled as asked — e.g. the " +
              "premium gapped outside the market-protection band. Both mean the " +
              "strategy may believe it holds a position it does not."
            }
            value={
              <span className="text-fg">
                {formatInt(summary?.orders_submitted)} /{" "}
                <span className="text-down-600 dark:text-down-400">
                  {formatInt(summary?.orders_rejected)}
                </span>{" "}
                /{" "}
                <span
                  className={
                    (summary?.orders_unfilled ?? 0) > 0
                      ? "text-warn-700 dark:text-warn-400 font-semibold"
                      : "text-muted"
                  }
                >
                  {formatInt(summary?.orders_unfilled)}
                </span>
                {(summary?.orders_awaiting_fill ?? 0) > 0 && (
                  <span className="text-muted text-xs">
                    {" "}
                    ({summary?.orders_awaiting_fill} awaiting)
                  </span>
                )}
              </span>
            }
          />
        </div>
      </div>

      {/* Admin revoked something this deployment needs. Deliberately does NOT
          stop a running deployment — abandoning a live position is worse than a
          few more minutes of access — so this explains why Start is disabled
          while Stop/Kill stay available. */}
      {d.policy_blocked && (
        <div className="text-sm text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded px-3 py-3">
          <span className="font-semibold">Access changed by an admin.</span>{" "}
          {d.policy_reason}
          {running && (
            <>
              {" "}
              This deployment keeps running until you stop it, but it cannot be
              started again.
            </>
          )}
        </div>
      )}

      {/* Day-is-over banner. `max_loss_hit` is the single persisted "no more
          entries today" flag; `stop_reason` says which gate set it. Rows from
          before 2026-11-02 have none, and fall back to the booked P&L's sign —
          which is how a kill switch on a green day once read "closed in
          profit". The day's state outlives the runner, so this stays up across
          a stop/kill/restart until the user resets. */}
      {dayState?.max_loss_hit && (
        <div
          className={
            dayStopKind === "profit"
              ? "text-sm text-up-700 dark:text-up-400 bg-up-50 dark:bg-up-500/10 border border-up-200 dark:border-up-400/30 rounded px-3 py-3 space-y-2"
              : "text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-3 space-y-2"
          }
        >
          <div>
            {dayStopKind === "kill_switch" ? (
              <>
                <span className="font-semibold">Kill switch engaged</span> —
                broker and deployment positions disagreed. Check the broker
                terminal; reset the deployment to trade again.
              </>
            ) : dayStopKind === "manual_kill" ? (
              <>
                <span className="font-semibold">
                  The deployment was killed.
                </span>{" "}
                No new entries today. IF you still want to continue, reset the
                deployment first.
              </>
            ) : dayStopKind === "profit" ? (
              <>
                <span className="font-semibold">
                  The day is DONE — it closed in profit.
                </span>{" "}
                No new entries today. IF you still want to continue, reset the
                deployment first.
              </>
            ) : dayStopKind === "max_flips" ? (
              <>
                <span className="font-semibold">
                  The day is DONE — the maximum number of direction flips was
                  reached.
                </span>{" "}
                No new entries today. IF you still want to continue, reset the
                deployment first.
              </>
            ) : (
              <>
                <span className="font-semibold">
                  MAX LOSS for the day is already done.
                </span>{" "}
                We recommend to stop trading today. IF you still want to
                continue, reset the deployment first.
              </>
            )}
          </div>
          <div className="text-xs opacity-80">
            Booked today ({dayState.trade_date}):{" "}
            {formatINR(dayState.realized_rupees)}
            {dayState.realized_points != null && (
              <> · {fmtPts(dayState.realized_points)}</>
            )}
            . Resetting clears the day's P&amp;L, points and day-stop flag for
            this deployment. Your order and fill history is kept.
          </div>
          {!asAdmin && (
            <Button
              variant={dayStopKind === "profit" ? "secondary" : "danger"}
              disabled={running || resetDay.isPending}
              title={
                running
                  ? "Stop the deployment before resetting the day"
                  : "Clear today's remembered P&L and day-stop flag"
              }
              onClick={() => {
                if (
                  confirm(
                    "Reset today for this deployment?\n\n" +
                      "This clears the day's remembered P&L, points and the " +
                      "day-stop flag, so the deployment can trade again today. " +
                      "Orders and fills are kept.\n\nThis cannot be undone.",
                  )
                )
                  resetDay.mutate();
              }}
            >
              {resetDay.isPending ? "Resetting…" : "Reset day"}
            </Button>
          )}
        </div>
      )}

      {/* A position the last Stop left with the broker. Stop deliberately
          abandons rather than closes, so this is where the user decides what
          the next session should believe. Shown before Start is pressed, not as
          an error after — Start is disabled until this is answered. */}
      {abandoned && !running && (
        <div className="text-sm text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-100 dark:border-warn-400/30 rounded px-3 py-3 space-y-2">
          <div>
            <span className="font-semibold">
              This deployment still holds an open position.
            </span>{" "}
            It was left with the broker when the deployment was stopped. Choose
            what this session should do before starting.
          </div>
          <div className="text-xs opacity-80">
            <span className="font-mono">{abandoned.symbol}</span> ·{" "}
            {abandoned.lots} lot{abandoned.lots === 1 ? "" : "s"} (
            {abandoned.quantity}) · avg {formatINR(abandoned.avg_price)} ·{" "}
            {abandoned.direction === "bull" ? "long" : "short"} the underlying ·
            opened {abandoned.trade_date}
            {abandoned.days_ago > 0 && (
              <>
                {" "}
                ({abandoned.days_ago} day
                {abandoned.days_ago === 1 ? "" : "s"} ago)
              </>
            )}
          </div>
          {abandoned.expired && (
            <div className="text-xs font-semibold">
              ⚠ This contract expired on {abandoned.expiry}. Keeping it is
              almost certainly wrong — it can no longer be traded.
            </div>
          )}
          {abandoned.broker_mismatch && (
            <div className="text-xs font-semibold">
              ⚠ Your broker reports {abandoned.broker_quantity}, not{" "}
              {abandoned.quantity}. The position may have been closed or changed
              outside this deployment.
            </div>
          )}
          {!asAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!startable || start.isPending}
                title="Carry the position into this session and manage it from here"
                onClick={() => start.mutate("adopt")}
                iconLeft={<Play size={14} />}
              >
                {start.isPending ? "Starting…" : "Start & keep position"}
              </Button>
              <Button
                variant="secondary"
                disabled={!startable || start.isPending}
                title="Forget this position and start with a clean book"
                onClick={() => {
                  if (
                    confirm(
                      "Start flat and forget the open position?\n\n" +
                        `${abandoned.symbol} · ${abandoned.lots} lot(s) will no ` +
                        "longer be tracked by this deployment. If it is still " +
                        "open with your broker you will have to manage it " +
                        "yourself.\n\nThis cannot be undone.",
                    )
                  )
                    start.mutate("flat");
                }}
              >
                Start flat
              </Button>
            </div>
          )}
        </div>
      )}

      {actionError && (
        <div className="text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2">
          {actionError}
        </div>
      )}

      {cloneNotice && (
        <div className="flex items-start justify-between gap-3 rounded border border-up-200 dark:border-up-400/30 bg-up-50 dark:bg-up-500/10 px-3 py-2 text-sm text-up-700 dark:text-up-400">
          <span>{cloneNotice}</span>
          <button
            type="button"
            onClick={() => setCloneNotice(null)}
            className="shrink-0 opacity-70 hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}

      {cloning && !asAdmin && (
        <div className="bg-surface-2 border border-border rounded p-4 space-y-3 text-sm">
          <div className="font-medium text-fg">
            Clone this deployment — and switch between paper and live
          </div>
          <p className="text-xs text-muted">
            Creates a <strong>new</strong> deployment with the same strategy,
            params, instruments, broker mapping, sizing and rails. Nothing about
            &ldquo;{d.name}&rdquo; changes. The copy starts with a clean slate —
            no orders, no fills, no day P&amp;L and no carried position — which
            is why this, rather than switching the mode of an existing
            deployment, is the safe way to move between paper and live.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-fg-subtle mb-1">
                Name
              </label>
              <input
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder={`${d.name} (copy)`}
                className="w-full border border-border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-subtle mb-1">
                Mode
              </label>
              {/* Two explicit buttons rather than a dropdown: switching between
                  paper and live is the main reason to clone at all, and a
                  collapsed <select> hid that badly enough that the feature read
                  as missing. */}
              <div className="flex gap-2">
                {(["paper", "live"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setCloneMode(m)}
                    className={`flex-1 rounded border px-3 py-2 text-sm capitalize transition ${
                      cloneMode === m
                        ? m === "live"
                          ? "border-warn-400 bg-warn-50 dark:bg-warn-500/10 text-warn-700 dark:text-warn-400 font-semibold"
                          : "border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400 font-semibold"
                        : "border-border text-muted hover:text-fg"
                    }`}
                  >
                    {m}
                    {m === d.mode && (
                      <span className="ml-1 text-[10px] font-normal opacity-70">
                        (same)
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {cloneMode === "live" && d.mode === "paper" && (
            <div className="text-xs text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded px-3 py-2">
              This copy will place <strong>real orders</strong> with the mapped
              broker as soon as you start it. Check the capital, sizing and risk
              rails before starting — paper values are often not what you want
              live.
            </div>
          )}
          {clone.isError && (
            <div className="text-xs text-down-700 dark:text-down-400">
              {apiErrorMessage(clone.error)}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              disabled={clone.isPending}
              onClick={() =>
                clone.mutate({
                  ...(cloneName.trim() ? { name: cloneName.trim() } : {}),
                  mode: cloneMode,
                })
              }
            >
              {clone.isPending ? "Creating…" : "Create clone"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setCloning(false);
                setSearchParams({}, { replace: true });
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {d.last_error && (
        <div className="text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2">
          <span className="font-medium">Last error:</span> {d.last_error}
        </div>
      )}

      {/* An order whose outcome is bad OR unknown. Deliberately as prominent as
          last_error: the consequence is the same either way — the strategy's
          idea of the position may no longer match the broker's. */}
      {summary?.fill_alert && (
        <div className="text-sm text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded px-3 py-2">
          <span className="font-medium">Order not confirmed filled:</span>{" "}
          {summary.fill_alert}
          <div className="text-xs mt-1 opacity-90">
            Check the position in your broker terminal. If it differs from the
            Positions tab, stop the deployment and reset the day before
            restarting.
          </div>
        </div>
      )}

      <Tabs
        tabs={[
          { key: "chart", label: "Chart" },
          { key: "positions", label: "Positions" },
          { key: "orders", label: "Orders", count: orders?.length ?? 0 },
          { key: "fills", label: "Fills", count: fills?.length ?? 0 },
          { key: "config", label: "Config" },
        ]}
      >
        {(active) => {
          if (active === "chart") {
            return (
              <DeploymentChartTab
                d={d}
                fills={fills ?? []}
                running={running}
                goldenState={summary?.golden_state}
              />
            );
          }
          if (active === "positions") {
            return (
              <Card title={running ? "Live (1s refresh)" : "Last known"}>
                <PositionsTable
                  positions={summary?.positions ?? []}
                  allPrices={summary?.last_prices ?? {}}
                  deploymentInstruments={d.instruments}
                />
              </Card>
            );
          }
          if (active === "orders") {
            return (
              <Card title={`Order log (${orders?.length ?? 0})`}>
                <OrdersTable orders={orders ?? []} />
              </Card>
            );
          }
          if (active === "fills") {
            return (
              <Card title={`Fills (${fills?.length ?? 0})`}>
                <FillsTable fills={fills ?? []} />
              </Card>
            );
          }
          return (
            <div className="space-y-6">
              <Card title="Configuration">
                <DefinitionList rows={configRows(d)} />
              </Card>
              <Card title="Instruments + broker routing">
                <InstrumentsTable d={d} />
              </Card>
            </div>
          );
        }}
      </Tabs>
    </div>
  );
}

/** Format deployment-symbol points P&L with an explicit sign and "pts" unit. */
/** Price cell: 2-decimal fixed, em-dash for missing. Accepts the API's
 *  decimal-as-string fields and the numeric deployment refs alike. */
function fmt2(v: string | number | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "—";
}

function fmtPts(v: number): string {
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1)} pts`;
}

function RuntimeStat({
  label,
  value,
  title,
}: {
  label: string;
  value: React.ReactNode;
  title?: string;
}) {
  return (
    <div title={title}>
      <div className="text-[10px] uppercase tracking-wide text-muted">{label}</div>
      <div className="text-sm font-mono font-semibold mt-0.5">{value}</div>
    </div>
  );
}

/** Live feed-health indicator: green while ticks are fresh, amber/red when the
 *  feed goes stale, with a one-click Reconnect that rebuilds the tick transport
 *  WITHOUT restarting the runner (the open position survives). */
function FeedHealth({
  running,
  secondsSinceTick,
  ticks,
  alert,
  onReconnect,
  reconnecting,
  reconnectError,
}: {
  running: boolean;
  secondsSinceTick: number | null;
  ticks: number | undefined;
  alert: string | null;
  /** Omitted for an admin viewing another user's deployment. */
  onReconnect?: () => void;
  reconnecting: boolean;
  /** Why the last Reconnect did not work (e.g. the token was rejected). */
  reconnectError?: string | null;
}) {
  if (!running) return <span className="text-muted">—</span>;
  // Stale if the watchdog flagged it, or no tick for >90s.
  const stale = !!alert || (secondsSinceTick != null && secondsSinceTick > 90);
  const never = secondsSinceTick == null;
  const dot = stale ? "bg-down-500" : never ? "bg-amber-500" : "bg-up-500";
  const ageLabel = never
    ? "no ticks yet"
    : secondsSinceTick < 2
      ? "live"
      : `${Math.round(secondsSinceTick)}s ago`;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        <span className={stale ? "text-down-600 dark:text-down-400" : "text-fg"}>
          {ageLabel}
        </span>
        <span className="text-muted text-xs">({formatInt(ticks)})</span>
      </div>
      {(stale || never) && onReconnect && (
        <button
          type="button"
          onClick={onReconnect}
          disabled={reconnecting}
          className="text-xs px-2 py-0.5 rounded border border-border hover:bg-elevated disabled:opacity-50 w-fit"
        >
          {reconnecting ? "Reconnecting…" : "Reconnect feed"}
        </button>
      )}
      {reconnectError && (stale || never) && (
        <span className="text-xs text-down-600 dark:text-down-400 max-w-xs">
          {reconnectError}
        </span>
      )}
    </div>
  );
}

function PositionsTable({
  positions,
  allPrices,
  deploymentInstruments,
}: {
  positions: DeploymentPositionView[];
  allPrices: Record<string, string>;
  deploymentInstruments: Deployment["instruments"];
}) {
  const symbolByKey: Record<string, string> = {};
  for (const i of deploymentInstruments) {
    const keyParts = [i.exchange, i.symbol];
    if (i.expiry) keyParts.push(i.expiry);
    if (i.strike) keyParts.push(`${i.strike}${i.instrument_type}`);
    symbolByKey[keyParts.join(":")] = i.symbol;
  }
  const positionKeys = new Set(positions.map((p) => p.instrument_key));
  const monitoredOnly = Object.entries(allPrices)
    .filter(([key]) => !positionKeys.has(key))
    .map(([key, price]) => ({
      instrument_key: key,
      symbol: symbolByKey[key] ?? key,
      price,
    }));

  if (positions.length === 0 && monitoredOnly.length === 0) {
    return <EmptyState title="No tick data yet" message="Once a tick lands, positions appear here." />;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted bg-surface-2/60">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Symbol</th>
            <th className="text-left px-3 py-2 font-medium">Side</th>
            <th className="text-right px-3 py-2 font-medium">Qty</th>
            <th className="text-right px-3 py-2 font-medium">Avg</th>
            <th className="text-right px-3 py-2 font-medium">Current</th>
            <th className="text-right px-3 py-2 font-medium" title="Underlying (deployment symbol) lots-weighted average entry">Depl. avg</th>
            <th className="text-right px-3 py-2 font-medium" title="Underlying (deployment symbol) latest price">Depl. current</th>
            <th className="text-right px-3 py-2 font-medium">Market value</th>
            <th className="text-right px-3 py-2 font-medium">Unrealized</th>
            <th className="text-right px-3 py-2 font-medium">Realized</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => (
            <tr key={p.instrument_key} className="border-t border-border/60 hover:bg-surface-2/40">
              <td className="px-3 py-2 font-mono">{p.symbol}</td>
              <td className="px-3 py-2">
                <Chip
                  tone={p.side === "LONG" ? "up" : p.side === "SHORT" ? "down" : "default"}
                  mono
                >
                  {p.side}
                </Chip>
              </td>
              <td className="px-3 py-2 font-mono text-right">{p.quantity}</td>
              <td className="px-3 py-2 font-mono text-right">{fmt2(p.avg_price)}</td>
              <td className="px-3 py-2 font-mono text-right">{fmt2(p.current_price)}</td>
              <td className="px-3 py-2 font-mono text-right">{fmt2(p.deployment_avg)}</td>
              <td className="px-3 py-2 font-mono text-right">{fmt2(p.deployment_current)}</td>
              <td className="px-3 py-2 font-mono text-right">{p.market_value ?? "—"}</td>
              <td className={`px-3 py-2 font-mono text-right ${signedClass(p.unrealized_pnl)}`}>
                {formatINR(p.unrealized_pnl, { sign: true })}
              </td>
              <td className={`px-3 py-2 font-mono text-right ${signedClass(p.realized_pnl)}`}>
                {formatINR(p.realized_pnl, { sign: true })}
              </td>
            </tr>
          ))}
          {monitoredOnly.map((m) => (
            <tr
              key={m.instrument_key}
              className="border-t border-border/60 text-fg-subtle"
            >
              <td className="px-3 py-2 font-mono">{m.symbol}</td>
              <td className="px-3 py-2">
                <Chip mono>FLAT</Chip>
              </td>
              <td className="px-3 py-2 font-mono text-right">0</td>
              <td className="px-3 py-2 font-mono text-right">—</td>
              <td className="px-3 py-2 font-mono text-right">{fmt2(m.price)}</td>
              <td className="px-3 py-2 font-mono text-right">—</td>
              <td className="px-3 py-2 font-mono text-right">—</td>
              <td className="px-3 py-2 font-mono text-right">—</td>
              <td className="px-3 py-2 font-mono text-right">—</td>
              <td className="px-3 py-2 font-mono text-right">—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Per-trade running average entry price for each fill. A "trade" is a position
 *  episode on one traded symbol (open → flat); the average is rebuilt from
 *  scratch each time the position returns to flat, so every trade is kept
 *  separate. Adds recompute the weighted average; reduces keep the entry basis. */
function computeTradeAvgPrices(fills: FillEvent[]): Record<string, number> {
  const chrono = [...fills].sort(
    (a, b) => parseTs(a.filled_at) - parseTs(b.filled_at) || (a.id < b.id ? -1 : 1),
  );
  let state: Record<string, { pos: number; avg: number }> = {};
  const out: Record<string, number> = {};
  let prevRealized: number | null = null;
  for (const f of chrono) {
    // A runner restart resets realized_pnl_after to 0 and drops all in-memory
    // (paper) positions with no closing fill. Clear ALL per-symbol tracking at
    // that boundary, else an orphaned pre-restart position keeps polluting the
    // average (it never returns to flat). Same reset signal as today's-P&L.
    const realized = Number(f.realized_pnl_after);
    if (prevRealized !== null && realized === 0 && prevRealized !== 0) state = {};
    prevRealized = realized;
    const s = state[f.symbol] ?? { pos: 0, avg: 0 };
    const px = Number(f.price);
    const signed = (f.side === "BUY" ? 1 : -1) * Number(f.quantity);
    if (s.pos === 0) {
      s.avg = px;
      s.pos = signed;
      out[f.id] = s.avg;
    } else if (Math.sign(s.pos) === Math.sign(signed)) {
      // Adding to the position → weighted-average the entry price in.
      s.avg = (s.avg * Math.abs(s.pos) + px * Math.abs(signed)) / (Math.abs(s.pos) + Math.abs(signed));
      s.pos += signed;
      out[f.id] = s.avg;
    } else {
      // Reducing / closing → show the trade's entry basis (avg unchanged).
      const next = s.pos + signed;
      out[f.id] = s.avg;
      if (next === 0) {
        s.pos = 0;
        s.avg = 0; // flat → next fill starts a fresh trade
      } else if (Math.sign(next) !== Math.sign(s.pos)) {
        s.pos = next;
        s.avg = px; // flipped past flat → new trade opens at this price
      } else {
        s.pos = next; // partial reduce
      }
    }
    state[f.symbol] = s;
  }
  return out;
}

function FillsTable({ fills }: { fills: FillEvent[] }) {
  const avgByFill = useMemo(() => computeTradeAvgPrices(fills), [fills]);
  const fmt4 = (v: string | number) => Number(v).toFixed(4);
  if (fills.length === 0) return <EmptyState title="No fills yet" />;
  return (
    <DataTable
      rows={fills}
      rowKey={(f) => f.id}
      columns={[
        {
          key: "ts",
          header: "Time (IST)",
          mono: true,
          render: (f) => (
            <span className="text-xs whitespace-nowrap">{formatIst(f.filled_at, { withSeconds: true })}</span>
          ),
        },
        { key: "symbol", header: "Symbol", mono: true, render: (f) => f.symbol },
        {
          key: "side",
          header: "Side",
          render: (f) => (
            <Chip tone={f.side === "BUY" ? "up" : "down"} mono>
              {f.side}
            </Chip>
          ),
        },
        { key: "qty", header: "Qty", align: "right", mono: true, render: (f) => formatInt(Number(f.quantity)) },
        { key: "price", header: "Price", align: "right", mono: true, render: (f) => fmt4(f.price) },
        {
          key: "avg",
          header: "Avg Price",
          align: "right",
          mono: true,
          render: (f) => (avgByFill[f.id] === undefined ? "—" : fmt4(avgByFill[f.id])),
        },
        { key: "fees", header: "Fees", align: "right", mono: true, render: (f) => fmt4(f.fees) },
        {
          key: "rpnl",
          header: "Realized P&L after",
          align: "right",
          mono: true,
          render: (f) => (
            <span className={signedClass(f.realized_pnl_after)}>{fmt4(f.realized_pnl_after)}</span>
          ),
        },
        {
          key: "coid",
          header: "Order id",
          mono: true,
          render: (f) => (
            <span className="text-xs text-muted truncate max-w-[180px] block" title={f.client_order_id}>
              {f.client_order_id}
            </span>
          ),
        },
      ]}
    />
  );
}

function OrdersTable({ orders }: { orders: OrderEvent[] }) {
  if (orders.length === 0) return <EmptyState title="No orders yet" />;
  return (
    <DataTable
      rows={orders}
      rowKey={(o) => o.id}
      columns={[
        {
          key: "ts",
          header: "Time (IST)",
          mono: true,
          render: (o) => (
            <span className="text-xs whitespace-nowrap">{formatIst(o.submitted_at, { withSeconds: true })}</span>
          ),
        },
        { key: "symbol", header: "Symbol", mono: true, render: (o) => o.symbol },
        {
          key: "side",
          header: "Side",
          render: (o) => (
            <Chip tone={o.side === "BUY" ? "up" : "down"} mono>
              {o.side}
            </Chip>
          ),
        },
        { key: "type", header: "Type", mono: true, render: (o) => o.order_type },
        { key: "product", header: "Product", mono: true, render: (o) => o.product },
        { key: "lots", header: "Lots", align: "right", mono: true, render: (o) => o.quantity_lots },
        {
          key: "lim",
          header: "Limit / Trigger",
          align: "right",
          mono: true,
          render: (o) =>
            `${o.limit_price ?? "—"}${o.trigger_price ? " / " + o.trigger_price : ""}`,
        },
        {
          key: "status",
          header: "Status",
          render: (o) =>
            o.rejected ? (
              <Chip tone="warn" mono>
                <span title={o.rejection_reason ?? ""}>REJECTED</span>
              </Chip>
            ) : (
              <Chip tone="up" mono>
                SUBMITTED
              </Chip>
            ),
        },
        { key: "tag", header: "Tag", mono: true, render: (o) => o.tag ?? "—" },
      ]}
    />
  );
}

function InstrumentsTable({ d }: { d: Deployment }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted bg-surface-2/60">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Symbol</th>
            <th className="text-left px-3 py-2 font-medium">Exchange</th>
            <th className="text-left px-3 py-2 font-medium">Type</th>
            <th className="text-left px-3 py-2 font-medium">Expiry</th>
            <th className="text-left px-3 py-2 font-medium">Strike</th>
            <th className="text-left px-3 py-2 font-medium">Lot</th>
            <th className="text-left px-3 py-2 font-medium">Broker conn</th>
          </tr>
        </thead>
        <tbody>
          {d.instruments.map((i, idx) => {
            const keyParts = [i.exchange, i.symbol];
            if (i.expiry) keyParts.push(i.expiry);
            if (i.strike) keyParts.push(`${i.strike}${i.instrument_type}`);
            const key = keyParts.join(":");
            return (
              <tr key={idx} className="border-t border-border/60">
                <td className="px-3 py-2 font-mono">{i.symbol}</td>
                <td className="px-3 py-2 font-mono">{i.exchange}</td>
                <td className="px-3 py-2 font-mono">{i.instrument_type}</td>
                <td className="px-3 py-2 font-mono">{i.expiry ?? "—"}</td>
                <td className="px-3 py-2 font-mono">{i.strike ?? "—"}</td>
                <td className="px-3 py-2 font-mono">{i.lot_size ?? 1}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {d.broker_map[key] ?? "(unmapped)"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function configRows(d: Deployment): Array<[string, React.ReactNode]> {
  return [
    ["strategy", <span className="font-mono">{d.strategy_key}</span>],
    ["mode", <span className="font-mono">{d.mode}</span>],
    ["timeframe", <span className="font-mono">{d.timeframe}</span>],
    [
      "capital",
      <span className="font-mono">
        {d.capital}
        {d.margin && d.margin !== "1" && d.margin !== "1.0000" ? ` × ${d.margin}` : ""}
      </span>,
    ],
    ["sizing mode", <span className="font-mono">{d.sizing_mode}</span>],
    [
      "sizing params",
      <code className="font-mono text-xs">{JSON.stringify(d.sizing_params)}</code>,
    ],
    ["strategy params", <code className="font-mono text-xs">{JSON.stringify(d.params)}</code>],
    [
      "risk rails",
      <code className="font-mono text-xs">{JSON.stringify(d.risk_rails)}</code>,
    ],
    ["created", formatIst(d.created_at)],
    ["updated", formatIst(d.updated_at)],
  ];
}

function DefinitionList({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="text-sm">
      {rows.map(([k, v], idx) => (
        <div
          key={idx}
          className="flex justify-between py-1.5 border-b border-border/60 last:border-0"
        >
          <dt className="text-muted mr-3">{k}</dt>
          <dd className="text-fg text-right break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Live chart tab — the deployment's base (underlying) symbol with the
// strategy's indicators + golden number + ladder levels, and live fills
// plotted as markers. Fills execute on the traded contract (option premium
// scale), so each marker is positioned at the UNDERLYING price at the fill
// time and coloured by the underlying-direction (BUY CE / SELL PE = long-ish).
// ---------------------------------------------------------------------------
function DeploymentChartTab({
  d,
  fills,
  running,
  goldenState,
}: {
  d: Deployment;
  fills: FillEvent[];
  running: boolean;
  goldenState?: DeploymentSummary["golden_state"];
}) {
  const base = d.instruments[0];

  const strategyDetail = useQuery({
    queryKey: ["strategy", d.strategy_key],
    queryFn: () => getStrategy(d.strategy_key),
    enabled: !!d.strategy_key,
  });

  // Fullscreen for the chart body (same pattern as ChartPage/BacktestDetail).
  const chartFsRef = useRef<HTMLDivElement | null>(null);
  const [isChartFs, setIsChartFs] = useState(false);
  useEffect(() => {
    function handler() {
      setIsChartFs(document.fullscreenElement === chartFsRef.current);
    }
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Fixed window at mount (last 7 days → tomorrow) so the query keys are stable
  // across renders; bars still poll within it while running.
  const [win] = useState(() => {
    const now = Date.now();
    return {
      start: new Date(now - 7 * 24 * 3600_000).toISOString(),
      end: new Date(now + 24 * 3600_000).toISOString(),
    };
  });
  const barsQuery = useQuery({
    queryKey: ["deployment-bars", d.id, d.timeframe, win.start],
    queryFn: () =>
      getBars({
        symbol: base.symbol,
        exchange: base.exchange,
        timeframe: d.timeframe,
        instrument_type: base.instrument_type,
        expiry: base.expiry ?? null,
        strike: base.strike ?? null,
        lot_size: base.lot_size,
        tick_size: base.tick_size,
        start: win.start,
        end: win.end,
      }),
    enabled: !!base,
    // Poll the store continuously (not only while running): the broker
    // live-tail below persists fresh candles into the store, and this picks
    // them up as confirmed history so the chart always has the latest data.
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });

  // Live session candles (recent completed + the forming bar), aggregated from
  // the runner's tick stream. Polled fast so the forming candle updates ~every
  // tick; fed to PriceChart's `liveBars` which patches without reloading.
  const liveBarsQuery = useQuery({
    queryKey: ["deployment-live-bars", d.id],
    queryFn: () => getDeploymentLiveBars(d.id),
    enabled: !!base && running,
    refetchInterval: running ? 1_500 : false,
  });
  const runnerLiveBars = liveBarsQuery.data ?? [];

  // Broker live-tail for the base symbol: the freshest few candles pulled
  // straight from the live source and upserted into the store. This runs
  // whether or not the deployment is live — the runner's in-memory candles are
  // never persisted, so once it stops they vanish from the chart; live-tail
  // backfills them from the broker and keeps the chart current at all times.
  const liveTailQuery = useQuery({
    queryKey: ["deployment-live-tail", d.id, d.timeframe],
    queryFn: () =>
      getLiveTail({
        symbol: base.symbol,
        exchange: base.exchange,
        timeframe: d.timeframe,
        instrument_type: base.instrument_type,
        expiry: base.expiry ?? null,
        strike: base.strike ?? null,
        lot_size: base.lot_size,
        tick_size: base.tick_size,
      }),
    enabled: !!base,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });

  // Merge the two live sources by timestamp. The runner's tick-aggregated bar
  // wins for a shared bucket (it carries the forming candle at tick
  // resolution); the broker tail fills everything the runner isn't producing
  // (e.g. after a stop, or bars before this runner started).
  const liveBars = useMemo(() => {
    const byTs = new Map<string, BarOut>();
    for (const b of liveTailQuery.data?.bars ?? []) byTs.set(b.ts, b);
    for (const b of runnerLiveBars) byTs.set(b.ts, b);
    return Array.from(byTs.values()).sort((a, b) => a.ts.localeCompare(b.ts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTailQuery.data, liveBarsQuery.data]);

  // The chart renders stored history when present, else the live session bars
  // alone (e.g. a fresh deployment whose symbol has no backfill yet). When
  // there's no stored history we seed the chart *once* from the first live
  // snapshot and keep that reference stable — `liveBars` then patches/append
  // new candles on top, so the chart doesn't fully reload every poll.
  const storedBars = barsQuery.data?.bars ?? [];
  const [liveSeed, setLiveSeed] = useState<BarOut[]>([]);
  useEffect(() => {
    if (storedBars.length === 0 && liveSeed.length === 0 && liveBars.length > 0) {
      setLiveSeed(liveBars);
    }
  }, [storedBars.length, liveSeed.length, liveBars]);
  const chartBars = storedBars.length ? storedBars : liveSeed;

  // Only let the live overlay patch the *tail* (the last stored candle onward)
  // — never rewrite confirmed history. Without this, completed live bars push
  // over older stored candles and clobber their volume.
  const lastStoredTs = storedBars.length
    ? Date.parse(storedBars[storedBars.length - 1].ts)
    : -Infinity;
  const liveTail = useMemo(
    () => liveBars.filter((b) => Date.parse(b.ts) >= lastStoredTs),
    [liveBars, lastStoredTs],
  );

  // Every non-manual source computes its per-day level on the backend off the
  // finest stored TF (first_candle_htp -> the window POC, window_high_low ->
  // that window's high or low); manual mode draws the constant param below.
  const isGolden = isGoldenFamily(d.strategy_key);
  const goldenSource = (d.params as Record<string, unknown>).golden_source;
  const goldenQuery = useQuery({
    queryKey: ["deployment-golden", d.id, win.start],
    queryFn: () => getDeploymentGoldenNumbers(d.id, win.start, win.end),
    enabled: isGolden && goldenSource !== "manual" && goldenSource != null,
    refetchInterval: running ? 30_000 : false,
  });

  // Seed indicators from the strategy's defaults resolved against the params.
  const [indicators, setIndicators] = useState<IndicatorInstance[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [editingIndicator, setEditingIndicator] =
    useState<IndicatorInstance | null>(null);
  useEffect(() => {
    if (seeded || !strategyDetail.data) return;
    const seed: IndicatorInstance[] = [];
    for (const spec of strategyDetail.data.default_indicators) {
      const inst = buildInstanceFromSpec(
        {
          type: spec.type,
          params: spec.params,
          pane: spec.pane,
          color: spec.color ?? undefined,
          label: spec.label ?? undefined,
        },
        d.params,
      );
      if (inst) seed.push(inst);
    }
    setIndicators(seed);
    setSeeded(true);
  }, [strategyDetail.data, seeded, d.params]);

  // Golden Number line: any injected source → per-day values from the backend;
  // manual → the constant param drawn across every loaded session.
  const goldenByDay = useMemo<Record<string, number> | undefined>(() => {
    if (!isGolden) return undefined;
    const map: Record<string, number> = {};
    if (goldenSource === "manual") {
      const val = Number((d.params as Record<string, unknown>).golden_number);
      // `<= 0` is not a level, it is an unset one: `golden_number` defaults to a
      // placeholder 0, and since `manual` became the default source (2026-10-08)
      // a config created straight from the defaults carries both. Drawing it
      // would put the golden line and every ladder line at zero and crush the
      // price axis flat trying to fit them. The form refuses to save it; this
      // covers a config posted to the API with no params at all.
      if (!Number.isFinite(val) || val <= 0 || chartBars.length === 0) {
        return undefined;
      }
      for (const b of chartBars) map[istDayKey(b.ts)] = val;
    } else {
      for (const g of goldenQuery.data ?? []) {
        const v = Number(g.value);
        if (Number.isFinite(v)) map[g.day] = v;
      }
    }
    // The RUNNER's own anchor wins for today. The map above is recomputed from
    // the store by a different code path, and when the two disagree the orders
    // follow the runner — so drawing the recomputed one puts ladder lines on
    // the chart that no order was ever priced against. That is precisely what
    // made both September 2026 incidents unreadable for hours.
    const live = goldenState?.golden;
    const lastBar = chartBars[chartBars.length - 1];
    // Only when that last bar really is TODAY. Pre-market — or on a stopped
    // deployment whose chart still shows history — the newest bar belongs to a
    // previous session, and stamping today's anchor onto it would draw a line
    // no order was ever priced against.
    if (live != null && lastBar && istDayKey(lastBar.ts) === istDayKey(new Date().toISOString())) {
      const v = Number(live);
      if (Number.isFinite(v)) map[istDayKey(lastBar.ts)] = v;
    }
    return Object.keys(map).length ? map : undefined;
  }, [isGolden, goldenSource, d.params, chartBars, goldenQuery.data, goldenState]);

  // Same comparison the backend makes, shown where the lines are drawn.
  const goldenMismatch = useMemo(() => {
    const live = goldenState?.golden;
    const lastBar = chartBars[chartBars.length - 1];
    if (!running || live == null || !lastBar) return null;
    const key = istDayKey(lastBar.ts);
    if (key !== istDayKey(new Date().toISOString())) return null;
    const recomputed = (goldenQuery.data ?? []).find((g) => g.day === key)?.value;
    if (recomputed == null) return null;
    const a = Number(live);
    const b = Number(recomputed);
    if (!Number.isFinite(a) || !Number.isFinite(b) || Math.abs(a - b) <= 0.05) return null;
    return { live: a, recomputed: b };
  }, [running, goldenState, chartBars, goldenQuery.data]);

  const ladderPoints = useMemo<number[] | undefined>(() => {
    if (!isGoldenFamily(d.strategy_key)) return undefined;
    const lp = (d.params as Record<string, unknown>).ladder_points;
    if (!Array.isArray(lp)) return undefined;
    const nums = lp.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    return nums.length ? nums : undefined;
  }, [d.strategy_key, d.params]);

  // Map live fills → chart markers. Fills are on the traded contract, so place
  // each at the underlying price at its timestamp (nearest loaded bar) and set
  // the underlying-direction side from CE/PE (BUY CE / SELL PE ≈ long).
  const trades = useMemo<BacktestTrade[]>(() => {
    const bars = chartBars;
    if (bars.length === 0 || fills.length === 0) return [];
    // parseTs (not Date.parse) so naive `filled_at` is read as UTC, matching
    // the tz-aware bar timestamps — otherwise every marker snaps to one candle.
    const barMs = bars.map((b) => parseTs(b.ts));
    return fills.map((f, idx) => {
      const t = parseTs(f.filled_at);
      // nearest bar with ts <= fill time (else the first bar)
      let lo = 0;
      for (let i = 0; i < barMs.length; i++) {
        if (barMs[i] <= t) lo = i;
        else break;
      }
      const underlyingPrice = bars[lo].close;
      const sym = f.symbol.toUpperCase();
      const isPE = sym.endsWith("PE");
      const side = isPE ? (f.side === "BUY" ? "SELL" : "BUY") : f.side;
      return {
        idx,
        ts: f.filled_at,
        side,
        symbol: f.symbol,
        quantity: f.quantity,
        price: String(underlyingPrice),
        fees: f.fees,
        realized_pnl_delta: "0",
        tag: null,
      };
    });
  }, [chartBars, fills]);

  if (!base) return <EmptyState title="No instrument on this deployment" />;

  return (
    <Card
      title={`${base.exchange}:${base.symbol} · ${d.timeframe}`}
      subtitle="Underlying with strategy indicators, the RUNNER's golden number, and live fills"
      actions={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            const el = chartFsRef.current;
            if (!el) return;
            if (!document.fullscreenElement) el.requestFullscreen?.();
            else document.exitFullscreen?.();
          }}
          title="Press Esc to exit fullscreen"
        >
          {isChartFs ? "× Exit fullscreen" : "⛶ Fullscreen"}
        </Button>
      }
    >
      <div
        ref={chartFsRef}
        className={isChartFs ? "bg-surface p-4 h-screen w-screen flex flex-col" : ""}
      >
        {running && goldenState?.golden_status === "provisional" && (
          <div className="mb-2 text-xs text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-100 dark:border-warn-400/30 rounded px-3 py-2">
            <b>Provisional golden number.</b> Today's line is the (H+L+C)/3 of
            the first candle. It is replaced by the opening-window level as
            soon as the broker has published the window's candles, normally
            within a minute.
          </div>
        )}
        {goldenMismatch && (
          <div className="mb-2 text-xs text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-100 dark:border-warn-400/30 rounded px-3 py-2">
            <b>Golden number mismatch.</b> The runner is anchored to{" "}
            <b>{goldenMismatch.live}</b>, but recomputing the opening window from
            the stored bars gives <b>{goldenMismatch.recomputed}</b>. The lines
            below follow the runner, because that is what the orders were priced
            against.
          </div>
        )}
      {barsQuery.isLoading && chartBars.length === 0 ? (
        <div className="text-sm text-muted p-4">Loading candles…</div>
      ) : chartBars.length === 0 ? (
        <EmptyState
          title="No candles for this symbol yet"
          message={
            running
              ? "Waiting for the first live ticks to form a candle…"
              : "Backfill this symbol/timeframe or wait for live bars to accumulate."
          }
        />
      ) : (
        <PriceChart
          bars={chartBars}
          liveBars={liveTail}
          trades={trades}
          indicators={indicators}
          onIndicatorChange={(id, patch) =>
            setIndicators((cur) =>
              cur.map((i) => (i.id === id ? { ...i, ...patch } : i)),
            )
          }
          editingIndicator={editingIndicator}
          onCloseIndicatorEdit={() => setEditingIndicator(null)}
          watermark={`${base.exchange}:${base.symbol}`}
          timeframe={d.timeframe}
          instrument={{
            symbol: base.symbol,
            exchange: base.exchange,
            instrument_type: base.instrument_type,
            expiry: base.expiry,
            strike: base.strike,
            lot_size: base.lot_size,
            tick_size: base.tick_size,
          }}
          goldenByDay={goldenByDay}
          ladderPoints={ladderPoints}
          heightClass={isChartFs ? "h-full" : "h-[520px]"}
        />
      )}
      </div>
    </Card>
  );
}

/** IST day key (YYYY-MM-DD) for a bar timestamp. Uses the same +5:30-then-UTC
 *  formula as the chart's internal golden-line lookup so keys match exactly. */
function istDayKey(ts: string): string {
  return new Date(Date.parse(ts) + 19_800_000).toISOString().slice(0, 10);
}
